import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiFetch } from '@/lib/api';
import { VIEW_SUFFIX_RE, canonKey, normKey, thumbSrc } from '@/lib/assetKeys';
import type { ProductionState } from '@/hooks/useProduction';

/** Local heuristic used only when the engine wrote no assembly manifest. */
function refsForShot(shotId: string, shotPlan: unknown, assets: ProductionState['assets']) {
  const shots =
    shotPlan && typeof shotPlan === 'object' && 'shots' in (shotPlan as Record<string, unknown>)
      ? (shotPlan as { shots: Record<string, unknown>[] }).shots
      : [];
  const shot = shots.find((sh) => sh.shot_id === shotId) as
    | { description?: string; roll?: string; location_id?: string; location?: string; location_name?: string }
    | undefined;
  const charSheets = assets.filter((a) => a.type === 'character' && !VIEW_SUFFIX_RE.test(a.shot_id));
  const desc = String(shot?.description || '').toUpperCase();
  const isARoll = String(shot?.roll || 'a_roll').toLowerCase() === 'a_roll';
  // Characters: named in the description, or all principals on A-roll (usually one host).
  const chars = charSheets.filter(
    (c) => desc.includes(normKey(c.shot_id).replace(/_/g, ' ')) || desc.includes(c.shot_id.toUpperCase()) || isARoll,
  );
  const views = (charId: string) =>
    assets.filter((a) => {
      if (a.type !== 'character') return false;
      const m = VIEW_SUFFIX_RE.exec(a.shot_id);
      return !!m && normKey(a.shot_id.slice(0, m.index)) === normKey(charId);
    });
  const locId = String(shot?.location_id || '').trim();
  const locName = String(shot?.location_name || shot?.location || '').trim();
  const location =
    assets.find((a) => a.type === 'location' && locId && a.location_id === locId) ||
    assets.find((a) => a.type === 'location' && locName && normKey(a.shot_id) === normKey(locName));
  return { chars, views, location };
}

/**
 * Strip shown inside the enlarge lightbox: the references this shot was built from.
 *
 * GROUND TRUTH: reads the engine's per-shot assembly manifest
 * (`keyframes/debug/{shot}_assembly.json`, written at generation time). Only when no
 * manifest exists (older productions) does it fall back to the local heuristic
 * above, clearly labeled as an estimate.
 */
export default function ShotReferencesStrip({
  shotId,
  productionId,
  shotPlan,
  assets,
  startFrame,
}: {
  shotId: string;
  productionId?: string;
  shotPlan: unknown;
  assets: ProductionState['assets'];
  /**
   * Video context: also show the shot's storyboard image — LOCKED as the clip's first
   * frame ('frame'), or sent as its STORYBOARD reference ('sheet': an 8-panel sheet,
   * no first frame) — so producers can confirm what the daily was built from.
   */
  startFrame?: 'frame' | 'sheet';
}) {
  const { t } = useTranslation('production');
  const [manifest, setManifest] = useState<{
    ref_names_in_order?: string[];
    /** Which view each ref is (front / faceclose / profile / sheet / look_anchor). */
    ref_views?: string[];
    location?: { matched_key?: string; plate_attached?: boolean };
  } | null>(null);
  const [manifestMissing, setManifestMissing] = useState(false);

  useEffect(() => {
    setManifest(null);
    setManifestMissing(false);
    if (!productionId || !shotId) return;
    let dead = false;
    apiFetch(`/api/assets/${productionId}/keyframes/debug/${shotId}_assembly.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => !dead && setManifest(j))
      .catch(() => !dead && setManifestMissing(true));
    return () => {
      dead = true;
    };
  }, [productionId, shotId]);

  const { chars, views, location } = refsForShot(shotId, shotPlan, assets);

  const thumb = (src: string | null, label: string, key: string) => (
    <div key={key} className="relative shrink-0">
      {src ? (
        <img src={thumbSrc(src)} alt={label} className="h-20 rounded border border-white/30 object-cover" />
      ) : (
        <div className="flex h-20 w-20 items-center justify-center rounded border border-white/30 text-[9px] text-white/60">
          {t('state.noImage', { ns: 'common' })}
        </div>
      )}
      <span className="absolute bottom-0 left-0 right-0 truncate bg-black/70 px-1 text-center text-[9px] text-white">
        {label}
      </span>
    </div>
  );

  // Video context: the storyboard image the clip was built from — its locked first
  // frame, or (sheet) its STORYBOARD reference. Distinct emerald border.
  const sheet = startFrame === 'sheet';
  const L = sheet
    ? {
        badge: 'refs.storyboardBadge',
        only: 'refs.storyboard',
        truth: 'refs.groundTruthWithStoryboard',
        estimated: 'refs.estimatedWithStoryboard',
      }
    : {
        badge: 'refs.startFrameBadge',
        only: 'refs.startFrame',
        truth: 'refs.groundTruthWithStart',
        estimated: 'refs.estimatedWithStart',
      };
  const startKf = startFrame ? assets.find((a) => a.type === 'keyframe' && normKey(a.shot_id) === normKey(shotId)) : undefined;
  const startFrameNode = startKf ? (
    <div key="startframe" className="relative shrink-0">
      <img
        src={thumbSrc(startKf.path)}
        alt={sheet ? 'storyboard' : 'start frame'}
        className="h-20 rounded border-2 border-emerald-400 object-cover"
      />
      <span className="absolute bottom-0 left-0 right-0 truncate bg-emerald-600/90 px-1 text-center text-[9px] font-semibold text-white">
        {t(L.badge)}
      </span>
    </div>
  ) : null;
  // Fallback render when there is nothing but the start frame to show.
  const startOnly = startFrameNode ? (
    <div className="mt-2 rounded-lg bg-black/70 p-2">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/70">{t(L.only)}</div>
      <div className="flex gap-1.5 overflow-x-auto">{startFrameNode}</div>
    </div>
  ) : null;

  if (manifest?.ref_names_in_order?.length) {
    // Resolve each transmitted ref name to a thumbnail from the asset list.
    // Names come from the engine assembly manifest verbatim:
    //   character → "NAME"   location → "LOCATION SET — <key>"   prop → "PROP — <NAME>"
    // Strip either dash (em-dash/hyphen — the engine has used both) and fall back
    // to a contains-match: a location's manifest key is its canonical entity key,
    // which can differ from the asset's stored shot_id by punctuation.
    const findByType = (type: string, key: string) => {
      const ck = canonKey(key);
      const nk = normKey(key);
      return (
        assets.find((a) => a.type === type && canonKey(a.shot_id) === ck) ||
        assets.find((a) => a.type === type && normKey(a.shot_id) === nk) ||
        assets.find((a) => a.type === type && (normKey(a.shot_id).includes(nk) || nk.includes(normKey(a.shot_id)))) ||
        null
      );
    };
    const findAsset = (name: string) => {
      if (name.startsWith('LOCATION SET')) {
        return findByType('location', name.replace(/^LOCATION SET\s*[—–-]?\s*/, ''));
      }
      if (name.startsWith('PROP')) {
        return findByType('prop', name.replace(/^PROP\s*[—–-]?\s*/, ''));
      }
      return assets.find((a) => a.type === 'character' && normKey(a.shot_id) === normKey(name)) || null;
    };
    // Per-view manifest (engine with the reference plan): one tile per image sent,
    // labelled with its view, instead of one "NAME ×N" sheet tile.
    const viewsOf = manifest.ref_views;
    if (viewsOf && viewsOf.length === manifest.ref_names_in_order.length) {
      const fold = (x: string) => canonKey(x.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
      const findView = (name: string, view: string) => {
        if (!view || view === 'sheet') return findAsset(name);
        return (
          assets.find((a) => {
            if (a.type !== 'character') return false;
            const m = VIEW_SUFFIX_RE.exec(a.shot_id);
            return !!m && m[1].toLowerCase() === view && fold(a.shot_id.slice(0, m.index)) === fold(name);
          }) || null
        );
      };
      return (
        <div className="mt-2 rounded-lg bg-black/70 p-2">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/70">
            {startFrameNode ? t(L.truth) : t('refs.groundTruth')}
          </div>
          <div className="flex gap-1.5 overflow-x-auto">
            {startFrameNode}
            {manifest.ref_names_in_order.map((name, i) => {
              const view = viewsOf[i];
              const isChar = !name.startsWith('LOCATION SET') && !name.startsWith('PROP');
              const a = isChar ? findView(name, view) : findAsset(name);
              const label = isChar && view ? `${name} · ${view.replace('_', ' ')}` : name;
              return thumb(a ? a.path : null, label, `gv-${i}`);
            })}
          </div>
        </div>
      );
    }
    // Collapse repeated per-view character entries into "NAME ×N".
    const counted: { name: string; n: number }[] = [];
    for (const n of manifest.ref_names_in_order) {
      const last = counted[counted.length - 1];
      if (last && last.name === n) last.n += 1;
      else counted.push({ name: n, n: 1 });
    }
    return (
      <div className="mt-2 rounded-lg bg-black/70 p-2">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/70">
          {startFrameNode ? t(L.truth) : t('refs.groundTruth')}
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {startFrameNode}
          {counted.map((c, i) => {
            const a = findAsset(c.name);
            return thumb(a ? a.path : null, c.n > 1 ? `${c.name} ×${c.n}` : c.name, `gt-${i}`);
          })}
        </div>
      </div>
    );
  }
  if (!manifestMissing) return startOnly; // manifest still loading — show at least the start frame
  if (chars.length === 0 && !location) return startOnly;
  return (
    <div className="mt-2 rounded-lg bg-black/70 p-2">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/70">
        {startFrameNode ? t(L.estimated) : t('refs.estimated')}
      </div>
      <div className="flex gap-1.5 overflow-x-auto">
        {startFrameNode}
        {location &&
          thumb(location.path, `SET${location.location_id ? ` ${location.location_id}` : ''}: ${location.shot_id}`, 'loc')}
        {chars.map((c) => [
          thumb(c.path, `${c.shot_id} (sheet)`, `sheet-${c.shot_id}`),
          ...views(c.shot_id).map((v) =>
            thumb(v.path, (VIEW_SUFFIX_RE.exec(v.shot_id)?.[1] || '').toLowerCase(), `v-${v.shot_id}`),
          ),
        ])}
      </div>
    </div>
  );
}
